import { useState } from 'react';
import { api } from '../lib/api';

/**
 * Small modal for the Call / Schedule quick actions. Lets the user edit the
 * task title, pick a due date, and add a note before it's created in
 * Next-Task.ai (a logged Call also saves the note as a file note server-side).
 */
export function NextTaskDialog({
  clientId,
  clientName,
  kind,
  onClose,
  onCompleted,
}: {
  clientId: string;
  clientName: string;
  kind: 'call' | 'schedule';
  onClose: () => void;
  onCompleted: (msg: string) => void;
}) {
  const isCall = kind === 'call';
  const addDays = (d: number) => new Date(Date.now() + d * 86_400_000).toISOString().slice(0, 10);

  const [title, setTitle] = useState(isCall ? `Call ${clientName}` : `Schedule meeting with ${clientName}`);
  const [dueDate, setDueDate] = useState(isCall ? addDays(1) : addDays(7));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api<{ data: { sent: boolean; fileNoteLogged: boolean } }>(
        `/api/clients/${clientId}/next-task`,
        {
          method: 'POST',
          body: JSON.stringify({
            kind,
            title: title.trim(),
            dueDate: dueDate || undefined,
            note: note.trim() || undefined,
          }),
        },
      );
      const where = r.data.sent ? 'in Next-Task' : '(Next-Task offline — recorded)';
      onCompleted(isCall ? `Call logged ${where}` : `Follow-up scheduled ${where}`);
      onClose();
    } catch {
      setError('Could not create the task — please retry.');
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={isCall ? 'Log a call' : 'Schedule a follow-up'}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="bg-paper border border-hairline rounded shadow-lg w-full max-w-md p-4">
        <h2 className="qa-h3 mb-3">{isCall ? 'Log a call' : 'Schedule a follow-up'}</h2>
        <div className="space-y-3">
          <div>
            <label className="qa-label" htmlFor="nt-title">Task title</label>
            <input
              id="nt-title"
              className="qa-input w-full"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nt-due">Due date</label>
            <input
              id="nt-due"
              type="date"
              className="qa-input w-full"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
          <div>
            <label className="qa-label" htmlFor="nt-note">
              Note {isCall ? '(also saved as a file note)' : '(optional)'}
            </label>
            <textarea
              id="nt-note"
              rows={3}
              className="qa-textarea w-full"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={isCall ? 'What was discussed…' : 'Context for the follow-up…'}
            />
          </div>
          {error && <p className="text-[11px] text-rose-700" role="alert">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" className="btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving || !title.trim()}>
            {saving ? 'Creating…' : isCall ? 'Log call' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  );
}
